// src/api/api.js
//const API_BASE_URL = 'http://localhost:8000';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export { API_BASE_URL };

/**
 * Sesión expirada: limpia el token y avisa a la app para que cierre sesión.
 * useAuth escucha 'auth:expired' y resetea el usuario.
 */
export const SESSION_EXPIRED_MSG = 'Tu sesión expiró. Iniciá sesión de nuevo.';

const handleSessionExpired = () => {
  localStorage.removeItem('token');
  window.dispatchEvent(new Event('auth:expired'));
};

/**
 * Extrae un mensaje legible del cuerpo de error de FastAPI.
 * En los 422 `detail` es un array de errores de validación, no un string,
 * así que pasarlo crudo a `new Error()` produce mensajes ilegibles.
 */
export const parseApiError = (body, fallback = 'Error inesperado') => {
  const detail = body?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map(d => {
      const campo = Array.isArray(d?.loc) ? d.loc.filter(l => l !== 'body').join('.') : '';
      return campo ? `${campo}: ${d.msg}` : d?.msg;
    }).filter(Boolean);
    return msgs.length > 0 ? msgs.join(' · ') : fallback;
  }
  return fallback;
};

/** fetch autenticado: adjunta el Bearer y convierte 401/403 en cierre de sesión. */
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  if (!token) {
    handleSessionExpired();
    throw new Error(SESSION_EXPIRED_MSG);
  }
  const response = await fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), 'Authorization': `Bearer ${token}` },
  });
  if (response.status === 401 || response.status === 403) {
    handleSessionExpired();
    throw new Error(SESSION_EXPIRED_MSG);
  }
  return response;
};

export const api = {
  // Categories
  async fetchCategories(pais = 'AR') {
    try {
      const response = await fetch(`${API_BASE_URL}/categories?pais=${encodeURIComponent(pais)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Product Categories
  async fetchProductCategories(store = null) {
    try {
      let url = `${API_BASE_URL}/product-categories`;
      if (store) {
        url += `?store=${encodeURIComponent(store)}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching product categories:', error);
      throw error;
    }
  },

  // Products
  async fetchProducts(params = {}) {
    const {
      store = null,
      page = 1,
      limit = 50,
      filters = {},
      pais = 'AR',
      orden = 'relevancia'
    } = params;

    try {
      let url = `${API_BASE_URL}/products?limite=${limit}&offset=${(page - 1) * limit}&pais=${encodeURIComponent(pais)}`;

      if (store) url += `&store=${encodeURIComponent(store)}`;

      if (filters.categoria && filters.categoria.length > 0)
        url += `&categoria_producto=${encodeURIComponent(filters.categoria.join(','))}`;

      if (filters.precioMin && filters.precioMin > 0)
        url += `&precio_min=${filters.precioMin}`;

      if (filters.precioMax && filters.precioMax > 0)
        url += `&precio_max=${filters.precioMax}`;

      if (filters.condicion)
        url += `&condicion=${encodeURIComponent(filters.condicion)}`;

      if (filters.envioGratis === true)
        url += `&envio_gratis=true`;

      if (filters.descuentoMin && filters.descuentoMin > 0)
        url += `&descuento_min=${filters.descuentoMin}`;

      if (filters.esInternacional !== null && filters.esInternacional !== undefined)
        url += `&es_internacional=${filters.esInternacional}`;

      if (filters.departamento)
        url += `&departamento=${encodeURIComponent(filters.departamento)}`;

      if (orden && orden !== 'relevancia')
        url += `&orden=${encodeURIComponent(orden)}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  /**
   * Cuántos productos quedarían con cada valor de cada filtro.
   *
   * Toma los mismos filtros que `fetchProducts` y devuelve las cuentas para esa
   * selección, para que el panel ofrezca sólo lo que tiene resultados. Devuelve
   * {} si algo falla: sin contadores el panel sigue funcionando igual que antes.
   */
  async fetchProductFacetas(params = {}) {
    const { store = null, filters = {}, pais = 'AR' } = params;
    try {
      const qs = new URLSearchParams({ pais });
      if (store) qs.append('store', store);
      if (filters.categoria?.length > 0) qs.append('categoria_producto', filters.categoria.join(','));
      if (filters.precioMin > 0)         qs.append('precio_min', filters.precioMin);
      if (filters.precioMax > 0)         qs.append('precio_max', filters.precioMax);
      if (filters.condicion)             qs.append('condicion', filters.condicion);
      if (filters.envioGratis === true)  qs.append('envio_gratis', 'true');
      if (filters.descuentoMin > 0)      qs.append('descuento_min', filters.descuentoMin);
      if (filters.esInternacional !== null && filters.esInternacional !== undefined)
        qs.append('es_internacional', filters.esInternacional);
      if (filters.departamento)          qs.append('departamento', filters.departamento);

      const response = await fetch(`${API_BASE_URL}/products/facetas?${qs}`);
      if (!response.ok) return {};
      return await response.json();
    } catch (error) {
      console.error('Error fetching facetas:', error);
      return {};
    }
  },

  /** Departamentos del catálogo general, con su conteo de ofertas. */
  async fetchProductosDepartamentos(pais = 'AR') {
    try {
      const response = await fetch(`${API_BASE_URL}/products/departamentos?pais=${encodeURIComponent(pais)}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching departamentos:', error);
      return { departamentos: [], total_ofertas: 0 };
    }
  },

  // Product categories for a store/country
  async fetchCategoriesForStore(store = null, pais = 'AR') {
    try {
      let url = `${API_BASE_URL}/product-categories?pais=${encodeURIComponent(pais)}`;
      if (store) url += `&store=${encodeURIComponent(store)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching categories for store:', error);
      return { categories: [] };
    }
  },

  // Top Chollos by store
  async fetchTopChollos(pais = 'AR') {
    try {
      const response = await fetch(`${API_BASE_URL}/api/top-chollos-by-store?pais=${encodeURIComponent(pais)}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching top chollos:', error);
      throw error;
    }
  },

  // Top Chollos by category
  async fetchTopChollosByCategory(pais = 'AR') {
    try {
      const response = await fetch(`${API_BASE_URL}/api/top-chollos-by-category?pais=${encodeURIComponent(pais)}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching top chollos by category:', error);
      throw error;
    }
  },

  // Destacados AR — el estante con link de afiliado generado a mano
  // (ver docs/PLAN-DESTACADOS-AR.md). Sólo tiene sentido para Argentina.
  async fetchDestacadosAr() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/destacados-ar`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching destacados AR:', error);
      throw error;
    }
  },

  // Search Products
  async searchProducts(query, limit = 50, { pais, store } = {}) {
    try {
      const qs = new URLSearchParams({ limite: String(limit) });
      // Sin país, el backend busca en la tabla entera y devuelve ofertas de
      // otro catálogo —en euros— dentro de una vitrina argentina.
      if (pais)  qs.set('pais', pais);
      if (store) qs.set('store', store);
      const response = await fetch(
        `${API_BASE_URL}/products/search/${encodeURIComponent(query)}?${qs}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  },

  // Authentication
  /**
   * Recupera la sesión guardada, o null si no hay ninguna válida.
   *
   * **Sólo un 401 o un 403 borran el token.** Antes lo borraba cualquier
   * respuesta que no fuera `ok`, y también cualquier error de red. Eso tenía
   * una consecuencia concreta: al forzar la actualización de la PWA la app se
   * recarga y pide `/me` de inmediato —justo cuando el deploy acaba de
   * reiniciar la API—. Bastaba un 502 de medio segundo, o un fetch que no
   * llegara a salir, para que el usuario apareciera deslogueado con la sesión
   * todavía vigente.
   *
   * «No pude comprobarlo» y «el token no vale» son cosas distintas. Ante la
   * duda se conserva el token: si de verdad estuviera vencido, la primera
   * llamada autenticada devolverá 401 y `authFetch` emitirá `auth:expired`,
   * que es quien cierra la sesión de verdad.
   */
  async checkAuthStatus() {
    const token = localStorage.getItem('token');
    if (!token) return null;

    // Un par de reintentos cortos, porque el caso típico es la API todavía
    // levantando después de un deploy. Con esperas crecientes, para no
    // insistirle a un servidor que aún no acepta conexiones.
    const INTENTOS = 3;
    for (let intento = 0; intento < INTENTOS; intento++) {
      try {
        const response = await fetch(`${API_BASE_URL}/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          // Sesión deslizante: cada apertura de la app renueva el token acá.
          // Mientras se siga abriendo dentro de la ventana de expiración
          // configurada en el backend, no se cierra sesión nunca.
          const tokenRenovado = response.headers.get('X-Refreshed-Token');
          if (tokenRenovado) localStorage.setItem('token', tokenRenovado);
          return await response.json();
        }

        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          return null;
        }
        // 5xx o cualquier otra cosa: el problema es del servidor, no del token.
      } catch (error) {
        // Sin red, o la API sin aceptar conexiones todavía.
      }

      if (intento < INTENTOS - 1) {
        await new Promise(r => setTimeout(r, 400 * (intento + 1)));
      }
    }

    // Se agotaron los intentos sin una respuesta clara. El token se conserva:
    // lo resolverá la próxima carga, o la próxima llamada autenticada.
    console.warn('No se pudo verificar la sesión; se conserva el token.');
    return null;
  },

  // Wishlist
  async fetchWishlist() {
    const token = localStorage.getItem('token');
    if (!token) return [];

    try {
      const response = await fetch(`${API_BASE_URL}/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      return [];
    }
  },

  async addToWishlist(productId) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ product_id: productId, user_id: 0 })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to wishlist');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    }
  },

  async removeFromWishlist(productId) {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/remove/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return response.ok;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return false;
    }
  },

  // Tracking
  async fetchTrackedProducts(activeOnly = false) {
    const url = `${API_BASE_URL}/tracking${activeOnly ? '?active_only=true' : ''}`;
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Error al obtener lista de seguimiento');
    return response.json();
  },

  /**
   * Con cuántas ofertas coincidiría una alerta, sin crearla.
   * El backend usa el mismo criterio que el bot, así que lo que promete acá es
   * lo que después llega por Telegram.
   */
  async previewTracking(data) {
    const response = await authFetch(`${API_BASE_URL}/tracking/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiError(err, 'No se pudo consultar la vista previa'));
    }
    return response.json();
  },

  async addTrackedProduct(data) {
    const response = await authFetch(`${API_BASE_URL}/tracking`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiError(err, 'Error al agregar producto'));
    }
    return response.json();
  },

  async updateTrackedProduct(id, data) {
    const response = await authFetch(`${API_BASE_URL}/tracking/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(parseApiError(err, 'Error al actualizar producto'));
    }
    return response.json();
  },

  async deleteTrackedProduct(id) {
    const response = await authFetch(`${API_BASE_URL}/tracking/${id}`, { method: 'DELETE' });
    return response.ok;
  },

  // Viajes (turismo)
  async fetchViajes(params = {}) {
    const { categoria, origen, destino_ciudad, busqueda } = params;
    try {
      const qs = new URLSearchParams();
      if (categoria)      qs.append('categoria',      categoria);
      if (origen)         qs.append('origen',         origen);
      if (destino_ciudad) qs.append('destino_ciudad', destino_ciudad);
      if (busqueda)       qs.append('busqueda',       busqueda);
      const url = `${API_BASE_URL}/viajes${qs.toString() ? '?' + qs : ''}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching viajes:', error);
      throw error;
    }
  },

  async fetchViajesFiltros() {
    try {
      const response = await fetch(`${API_BASE_URL}/viajes/filtros`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching viajes filtros:', error);
      return { categorias: [], origenes: [], destinos: [] };
    }
  },

  // Exclusivos: productos_ar (tiendas VTEX argentinas) o productos_es (moda de
  // outlet española). Es la misma ruta para los dos catálogos; `pais` decide
  // cuál sirve el backend, que por dentro usa consultas distintas.
  async fetchExclusivos(params = {}) {
    const { tienda, categoria, busqueda, descuento_min, pais, talla, solo_ultimas } = params;
    try {
      const qs = new URLSearchParams();
      if (tienda)        qs.append('tienda',        tienda);
      if (categoria)     qs.append('categoria',     categoria);
      if (busqueda)      qs.append('busqueda',      busqueda);
      if (descuento_min) qs.append('descuento_min', descuento_min);
      if (pais)          qs.append('pais',          pais);
      if (talla)         qs.append('talla',         talla);
      if (solo_ultimas)  qs.append('solo_ultimas',  'true');
      const url = `${API_BASE_URL}/exclusivos${qs.toString() ? '?' + qs : ''}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching exclusivos:', error);
      throw error;
    }
  },

  /** Rieles ya armados para la vitrina de Exclusivos (transversales + departamentos). */
  async fetchExclusivosVitrina(pais) {
    try {
      const qs = pais ? `?pais=${encodeURIComponent(pais)}` : '';
      const response = await fetch(`${API_BASE_URL}/exclusivos/vitrina${qs}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching exclusivos vitrina:', error);
      return { transversales: [], departamentos: [], total_ofertas: 0 };
    }
  },

  /**
   * Ofertas cuyo precio bajó respecto de la corrida anterior del extractor.
   * Devuelve [] si todavía no hay dos corridas registradas por producto.
   */
  async fetchExclusivosBajaron(limite = 200, pais) {
    try {
      const qs = pais ? `&pais=${encodeURIComponent(pais)}` : '';
      const response = await fetch(`${API_BASE_URL}/exclusivos/bajaron?limite=${limite}${qs}`);
      if (!response.ok) return [];
      return await response.json();
    } catch {
      return [];
    }
  },

  async fetchExclusivosFiltros(pais) {
    try {
      const qs = pais ? `?pais=${encodeURIComponent(pais)}` : '';
      const response = await fetch(`${API_BASE_URL}/exclusivos/filtros${qs}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching exclusivos filtros:', error);
      return { tiendas: [], categorias: [] };
    }
  },

  /**
   * Precio mínimo histórico por lote.
   * `fuente` indica de qué catálogo son los ids: 'productos' (ML),
   * 'productos_ar' (tiendas AR) o 'productos_es' (moda española). Los dos
   * últimos se resuelven por external_id porque sus extractores guardaban el
   * historial sin producto_id.
   */
  /**
   * Serie completa de precios observados, para el gráfico de la etiqueta
   * "Histórico". Se pide la serie y no solo el mínimo porque con dos
   * observaciones afirmar "precio mínimo histórico" es engañoso: el gráfico
   * deja que el usuario juzgue.
   */
  async fetchPriceHistorySeriesBatch(productIds, fuente = 'productos') {
    if (!productIds || productIds.length === 0) return {};
    try {
      const ids = productIds.join(',');
      const response = await fetch(
        `${API_BASE_URL}/price-history/series-batch?ids=${ids}&fuente=${encodeURIComponent(fuente)}`
      );
      if (!response.ok) return {};
      return await response.json();
    } catch {
      return {};
    }
  },

  /**
   * Anota que el visitante abrió una oferta.
   *
   * No devuelve nada ni se espera: es telemetría, y no puede demorar ni un
   * milisegundo la apertura del enlace. Se usa `sendBeacon`, que entrega la
   * petición aunque la pestaña pierda el foco o se cierre — con `fetch` normal
   * el navegador cancela lo que quede en vuelo al cambiar de página, y
   * justamente eso es lo que pasa al abrir la tienda.
   *
   * `sendBeacon` no admite cabeceras, así que el token va en el cuerpo. El
   * backend lo acepta igual y, si no hay sesión, anota el clic sin usuario.
   */
  registrarClic(productoId) {
    if (!productoId) return;
    const url = `${API_BASE_URL}/productos/${productoId}/clic`;
    try {
      const token = localStorage.getItem('token');
      const cuerpo = new Blob(
        [JSON.stringify({ token: token || null })],
        { type: 'application/json' },
      );
      if (navigator.sendBeacon && navigator.sendBeacon(url, cuerpo)) return;
    } catch {
      // sendBeacon no disponible o bloqueado: se intenta por fetch.
    }
    try {
      fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
    } catch {
      // Un clic sin registrar no es motivo para romperle la navegación a nadie.
    }
  },

  async fetchPriceHistoryBatch(productIds, fuente = 'productos') {
    if (!productIds || productIds.length === 0) return {};
    try {
      const ids = productIds.join(',');
      const response = await fetch(
        `${API_BASE_URL}/price-history/min-batch?ids=${ids}&fuente=${encodeURIComponent(fuente)}`
      );
      if (!response.ok) return {};
      return await response.json();
    } catch {
      return {};
    }
  },
};

export const fetchSizes = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.store) {
      queryParams.append('store', params.store);
    }
    
    if (params.categoria_producto) {
      if (Array.isArray(params.categoria_producto)) {
        queryParams.append('categoria_producto', params.categoria_producto.join(','));
      } else {
        queryParams.append('categoria_producto', params.categoria_producto);
      }
    }

    const url = queryParams.toString() 
      ? `${API_BASE_URL}/filters/sizes?${queryParams}` 
      : `${API_BASE_URL}/filters/sizes`;
    
    console.log('🔍 Fetching sizes from:', url); // Debug
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('📦 Sizes response:', data); // Debug
    
    return {
      sizes: data.sizes || [],
      filters_applied: data.filters_applied || {}
    };
  } catch (error) {
    console.error('Error fetching sizes:', error);
    return { sizes: [], filters_applied: {} };
  }
};